import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, keyframes, styled } from "@mui/material/styles";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BrushRoundedIcon from "@mui/icons-material/BrushRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import StraightenRoundedIcon from "@mui/icons-material/StraightenRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import ImageSearchRoundedIcon from "@mui/icons-material/ImageSearchRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import PhotoSizeSelectLargeRoundedIcon from "@mui/icons-material/PhotoSizeSelectLargeRounded";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AlternateEmailRoundedIcon from "@mui/icons-material/AlternateEmailRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const RAW_API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const SITE_NAME = "Art For My Room";
const CONTACT_EMAIL = "info@artformyroom.com";

const TURQUOISE = "#00B4A6";
const TURQUOISE_DARK = "#00796B";
const TURQUOISE_LIGHT = "#5FF5EA";
const TURQUOISE_MIST = "#DDFBF8";
const TURQUOISE_PALE = "#F4FFFD";

const GOLD = "#D4AF37";
const GOLD_DARK = "#8A6500";
const GOLD_MIST = "#FFF3CF";

const BURGUNDY = "#7A1232";
const BURGUNDY_DARK = "#4A071D";
const BURGUNDY_MIST = "#FBEAF0";

const INK = "#071313";
const WHITE = "#FFFFFF";
const OFF_WHITE = "#FBFFFE";
const MUTED = "rgba(7, 19, 19, 0.66)";
const LINE = "rgba(7, 19, 19, 0.10)";

const HEADING_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";
const MONO_FONT = "'JetBrains Mono', 'Fira Code', monospace";

const mediumOptions = [
  "Oil on canvas",
  "Acrylic on canvas",
  "Watercolour",
  "Mixed media",
  "Ink / drawing",
  "Charcoal",
  "Digital artwork",
  "Photography",
  "Other",
];

const artistLevels = [
  { value: "student", label: "Student / early artist" },
  { value: "emerging", label: "Emerging artist" },
  { value: "active", label: "Active selling artist" },
  { value: "established", label: "Established artist" },
];

const subjectOptions = [
  "Abstract",
  "Seascape / ocean",
  "Landscape",
  "Floral / botanical",
  "Portrait / figure",
  "Still life",
  "Urban / architecture",
  "Interior",
  "Spiritual / symbolic",
  "Minimal / atmospheric",
  "Other",
];

const countryOptions = [
  { value: "GB", label: "United Kingdom" },
  { value: "AL", label: "Albania" },
  { value: "US", label: "United States" },
  { value: "IT", label: "Italy" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "ES", label: "Spain" },
  { value: "GR", label: "Greece" },
  { value: "OTHER", label: "Other" },
];

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const shimmer = keyframes`
  0% { transform: translateX(-130%); }
  100% { transform: translateX(130%); }
`;

const PageWrap = styled(Box)({
  minHeight: "100vh",
  background:
    `radial-gradient(circle at 8% 7%, ${alpha(TURQUOISE, 0.18)}, transparent 30%),
     radial-gradient(circle at 88% 14%, ${alpha(GOLD, 0.22)}, transparent 28%),
     radial-gradient(circle at 88% 72%, ${alpha(BURGUNDY, 0.14)}, transparent 30%),
     linear-gradient(180deg, #ffffff 0%, #f5fffd 44%, #fff7e2 100%)`,
  color: INK,
  fontFamily: BODY_FONT,
  overflow: "hidden",
});

const HeroWrap = styled(Paper)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 42,
  padding: theme.spacing(4, 2.5),
  color: WHITE,
  background:
    `radial-gradient(circle at 14% 16%, ${alpha(TURQUOISE_LIGHT, 0.28)}, transparent 32%),
     radial-gradient(circle at 86% 24%, ${alpha(GOLD, 0.22)}, transparent 28%),
     radial-gradient(circle at 86% 82%, ${alpha(BURGUNDY, 0.25)}, transparent 30%),
     linear-gradient(135deg, #061414 0%, #0A1F1D 52%, ${BURGUNDY_DARK} 100%)`,
  border: `1px solid ${alpha(WHITE, 0.14)}`,
  boxShadow: "0 34px 110px rgba(7,19,19,0.28)",
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6),
  },
}));

const GlassCard = styled(Card)({
  borderRadius: 32,
  background: alpha(WHITE, 0.94),
  backdropFilter: "blur(18px)",
  border: `1px solid ${alpha(WHITE, 0.76)}`,
  boxShadow: "0 22px 70px rgba(0,80,74,0.10)",
  height: "100%",
  overflow: "hidden",
});

const DarkCard = styled(Card)({
  borderRadius: 32,
  color: WHITE,
  background:
    `radial-gradient(circle at 12% 18%, ${alpha(TURQUOISE, 0.25)}, transparent 32%),
     radial-gradient(circle at 88% 22%, ${alpha(GOLD, 0.18)}, transparent 30%),
     radial-gradient(circle at 86% 82%, ${alpha(BURGUNDY, 0.25)}, transparent 32%),
     linear-gradient(135deg, #061414 0%, #151204 58%, #220716 100%)`,
  border: `1px solid ${alpha(WHITE, 0.14)}`,
  boxShadow: "0 24px 80px rgba(0,0,0,0.24)",
  overflow: "hidden",
});

const UploadArea = styled(Paper)(({ theme }) => ({
  borderRadius: 30,
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  border: `2px dashed ${alpha(TURQUOISE, 0.48)}`,
  background:
    `linear-gradient(180deg, ${alpha(TURQUOISE_MIST, 0.78)} 0%, ${alpha(WHITE, 0.92)} 100%)`,
  position: "relative",
  overflow: "hidden",
  transition: "all 0.25s ease",
  "&:hover": {
    transform: "translateY(-3px)",
    borderColor: GOLD,
    boxShadow: `0 20px 46px ${alpha(TURQUOISE, 0.14)}`,
  },
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "38%",
    left: "-45%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.72), transparent)",
    animation: `${shimmer} 4s ease-in-out infinite`,
  },
}));

const Field = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 18,
    backgroundColor: alpha(WHITE, 0.95),
  },
  "& .MuiSelect-icon": {
    color: TURQUOISE_DARK,
  },
});

const GradientButton = styled(Button)({
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 950,
  padding: "13px 24px",
  color: WHITE,
  background: `linear-gradient(135deg, ${TURQUOISE} 0%, ${GOLD} 62%, ${BURGUNDY} 100%)`,
  boxShadow: `0 16px 38px ${alpha(TURQUOISE, 0.24)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${TURQUOISE_DARK} 0%, #B99325 62%, ${BURGUNDY_DARK} 100%)`,
    transform: "translateY(-2px)",
  },
  "&.Mui-disabled": {
    background: "linear-gradient(135deg, #9bded8 0%, #e7cf79 100%)",
    color: alpha(WHITE, 0.86),
  },
});

const LightButton = styled(Button)({
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 850,
  padding: "11px 20px",
  color: TURQUOISE_DARK,
  border: `1.5px solid ${alpha(TURQUOISE, 0.35)}`,
  background: alpha(WHITE, 0.78),
  "&:hover": {
    background: TURQUOISE_MIST,
    borderColor: TURQUOISE,
  },
});

const BurgundyButton = styled(Button)({
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 900,
  padding: "11px 20px",
  color: WHITE,
  background: `linear-gradient(135deg, ${BURGUNDY}, ${BURGUNDY_DARK})`,
  boxShadow: `0 14px 32px ${alpha(BURGUNDY, 0.18)}`,
  "&:hover": {
    background: `linear-gradient(135deg, ${BURGUNDY_DARK}, ${BURGUNDY})`,
    transform: "translateY(-2px)",
  },
});

const PreviewFrame = styled(Box)({
  borderRadius: 28,
  overflow: "hidden",
  background: "#071313",
  border: `1px solid ${alpha(INK, 0.08)}`,
  boxShadow: "0 22px 58px rgba(0,0,0,0.14)",
});

function buildApiUrl(path) {
  const base = RAW_API_BASE_URL.replace(/\/+$/, "");
  const cleanPath = path.replace(/^\/+/, "");

  if (!base) {
    return `/${cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`}`;
  }

  if (base.endsWith("/api")) {
    return `${base}/${cleanPath.replace(/^api\//, "")}`;
  }

  return `${base}/${cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`}`;
}

function uniqueList(items) {
  return [
    ...new Set(
      items
        .filter(Boolean)
        .map((item) => String(item).trim())
        .filter(Boolean)
    ),
  ];
}

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72);
}

function rgbToHex(r, g, b) {
  const toHex = (n) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

function rgbToHsl(r, g, b) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;

    h /= 6;
  }

  return { h: Math.round(h * 360), s, l };
}

function getColourName(hsl) {
  const { h, s, l } = hsl;

  if (l < 0.16) return "black";
  if (l > 0.9 && s < 0.18) return "white";
  if (s < 0.12) return l > 0.55 ? "soft grey" : "deep grey";
  if (h < 16 || h >= 345) return "red";
  if (h < 40) return "orange";
  if (h < 66) return "gold";
  if (h < 145) return "green";
  if (h < 195) return "turquoise";
  if (h < 230) return "blue";
  if (h < 275) return "violet";
  if (h < 330) return "pink";

  return "colour";
}

function getOrientation(width, height) {
  if (!width || !height) return "Unknown";

  const ratio = width / height;

  if (ratio > 1.18) return "Landscape";
  if (ratio < 0.84) return "Portrait";

  return "Square / near-square";
}

function getMood(stats) {
  const energy =
    stats.avgSaturation > 0.5 || stats.contrast > 0.52
      ? "energetic"
      : stats.avgSaturation < 0.22
        ? "quiet"
        : "balanced";

  const light =
    stats.avgLightness > 0.64
      ? "light-filled"
      : stats.avgLightness < 0.34
        ? "dramatic"
        : "measured";

  const texture =
    stats.edgeScore > 0.38
      ? "textured"
      : stats.edgeScore < 0.18
        ? "soft"
        : "layered";

  const temperature =
    stats.warmth > 0.12 ? "warm" : stats.warmth < -0.12 ? "cool" : "neutral";

  const buyerFeeling =
    energy === "quiet"
      ? "calm, reflective and easy to live with"
      : energy === "energetic"
        ? "expressive, confident and visually active"
        : "balanced, composed and adaptable";

  return {
    energy,
    light,
    texture,
    temperature,
    buyerFeeling,
    phrase: `${light}, ${texture}, ${temperature} and ${energy}`,
  };
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read this image."));
    };

    img.src = url;
  });
}

function analysePixels(img) {
  const maxSize = 420;
  const scale = Math.min(1, maxSize / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, width, height);

  const { data } = ctx.getImageData(0, 0, width, height);
  const buckets = new Map();
  const gray = new Float32Array(width * height);

  let totalR = 0;
  let totalG = 0;
  let totalB = 0;
  let totalS = 0;
  let totalL = 0;
  let warmTotal = 0;
  let count = 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const alphaValue = data[i + 3];

      if (alphaValue < 40) continue;

      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];

      totalR += r;
      totalG += g;
      totalB += b;

      const hsl = rgbToHsl(r, g, b);
      totalS += hsl.s;
      totalL += hsl.l;
      warmTotal += (r - b) / 255;

      gray[y * width + x] = 0.299 * r + 0.587 * g + 0.114 * b;

      if (x % 3 === 0 && y % 3 === 0) {
        const qr = Math.round(r / 32) * 32;
        const qg = Math.round(g / 32) * 32;
        const qb = Math.round(b / 32) * 32;
        const key = `${qr},${qg},${qb}`;
        buckets.set(key, (buckets.get(key) || 0) + 1);
      }

      count += 1;
    }
  }

  let edgeTotal = 0;
  let edgeCount = 0;
  let minLum = 255;
  let maxLum = 0;

  for (let y = 1; y < height - 1; y += 2) {
    for (let x = 1; x < width - 1; x += 2) {
      const center = gray[y * width + x];
      const right = gray[y * width + x + 1];
      const down = gray[(y + 1) * width + x];

      const vertical = Math.abs(center - right) / 255;
      const horizontal = Math.abs(center - down) / 255;
      const edge = vertical + horizontal;

      edgeTotal += edge / 2;
      edgeCount += 1;

      minLum = Math.min(minLum, center);
      maxLum = Math.max(maxLum, center);
    }
  }

  const bucketTotal = [...buckets.values()].reduce((a, b) => a + b, 0);

  const palette = [...buckets.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 9)
    .map(([key, bucketCount]) => {
      const [r, g, b] = key
        .split(",")
        .map((value) => Math.max(0, Math.min(255, Number(value))));

      const hsl = rgbToHsl(r, g, b);

      return {
        hex: rgbToHex(r, g, b),
        rgb: [r, g, b],
        name: getColourName(hsl),
        percentage: Math.round((bucketCount / Math.max(1, bucketTotal)) * 100),
      };
    });

  const avgR = totalR / Math.max(1, count);
  const avgG = totalG / Math.max(1, count);
  const avgB = totalB / Math.max(1, count);

  return {
    pixelWidth: img.naturalWidth,
    pixelHeight: img.naturalHeight,
    averageHex: rgbToHex(avgR, avgG, avgB),
    avgSaturation: totalS / Math.max(1, count),
    avgLightness: totalL / Math.max(1, count),
    warmth: warmTotal / Math.max(1, count),
    edgeScore: edgeTotal / Math.max(1, edgeCount),
    contrast: (maxLum - minLum) / 255,
    colourVariety: buckets.size,
    palette,
  };
}

function guessTechnique(stats, selectedMedium) {
  const mediumText = selectedMedium.toLowerCase();

  if (selectedMedium && !mediumText.includes("other")) {
    return {
      technique: selectedMedium,
      confidence: "artist provided",
      reason: "The artist selected the medium, so this is more reliable than visual guessing.",
    };
  }

  if (stats.edgeScore > 0.42 && stats.contrast > 0.38 && stats.colourVariety > 16) {
    return {
      technique: "Oil or acrylic painting",
      confidence: "medium",
      reason: "The image shows texture, contrast and colour separation often seen in paint-based work.",
    };
  }

  if (stats.avgLightness > 0.64 && stats.avgSaturation < 0.34 && stats.edgeScore < 0.26) {
    return {
      technique: "Watercolour or soft wash",
      confidence: "low-medium",
      reason: "The image looks light, soft and less sharply textured, which can resemble watercolour or diluted media.",
    };
  }

  return {
    technique: "Mixed media or contemporary painting",
    confidence: "low",
    reason: "The image contains mixed visual signals, so the artist should confirm the exact technique.",
  };
}

function cleanMoney(value) {
  const number = Number(String(value || "").replace("£", "").replace(",", "").trim());
  return Number.isFinite(number) && number > 0 ? number : null;
}

function estimatePrice(form, stats) {
  const width = Number(form.widthCm);
  const height = Number(form.heightCm);

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  const areaCm = width * height;
  const areaIn = areaCm / 6.4516;

  const levelRates = {
    student: { low: 0.25, high: 0.65, min: 35 },
    emerging: { low: 0.55, high: 1.25, min: 70 },
    active: { low: 1.1, high: 2.7, min: 140 },
    established: { low: 2.4, high: 6.2, min: 280 },
  };

  const medium = form.medium.toLowerCase();
  let mediumFactor = 1;

  if (medium.includes("oil")) mediumFactor = 1.22;
  else if (medium.includes("acrylic")) mediumFactor = 1.05;
  else if (medium.includes("mixed")) mediumFactor = 1.12;
  else if (medium.includes("water")) mediumFactor = 0.88;
  else if (medium.includes("charcoal") || medium.includes("ink")) mediumFactor = 0.82;
  else if (medium.includes("digital") || medium.includes("photography")) mediumFactor = 0.58;

  const complexityFactor =
    stats.colourVariety > 30 || stats.edgeScore > 0.38
      ? 1.12
      : stats.colourVariety < 10 && stats.edgeScore < 0.18
        ? 0.92
        : 1;

  const rates = levelRates[form.artistLevel] || levelRates.emerging;

  const low = Math.max(
    rates.min,
    Math.round((areaIn * rates.low * mediumFactor * complexityFactor) / 5) * 5
  );

  const high = Math.max(
    low + 20,
    Math.round((areaIn * rates.high * mediumFactor * complexityFactor) / 5) * 5
  );

  const safeListing = Math.round(((low + high) / 2) / 5) * 5;
  const fasterSale = Math.max(35, Math.round((safeListing * 0.88) / 5) * 5);
  const roomOfferMinimum = Math.max(35, Math.round((safeListing * 0.82) / 5) * 5);
  const ambitious = Math.round((safeListing * 1.16) / 5) * 5;

  const currentPrice = cleanMoney(form.actualPrice);

  let currentPriceNote = "";

  if (currentPrice) {
    if (currentPrice < low) {
      currentPriceNote =
        "Your current price is below the guide. It may sell faster, but it may also undervalue the work.";
    } else if (currentPrice > high) {
      currentPriceNote =
        "Your current price is above the guide. Support it with story, provenance, framing, close-ups and a strong room mockup.";
    } else {
      currentPriceNote = "Your current price sits inside the suggested guide.";
    }
  }

  return {
    low,
    high,
    safeListing,
    fasterSale,
    roomOfferMinimum,
    ambitious,
    areaCm: Math.round(areaCm),
    areaIn: Math.round(areaIn),
    label: `£${low} - £${high}`,
    safeListingLabel: `£${safeListing}`,
    fasterSaleLabel: `£${fasterSale}`,
    offerMinimumLabel: `£${roomOfferMinimum}`,
    ambitiousLabel: `£${ambitious}`,
    currentPriceNote,
  };
}

function buildPhotoChecklist(stats, file) {
  const megapixels = (stats.pixelWidth * stats.pixelHeight) / 1000000;

  return [
    {
      good: megapixels >= 1.2,
      title: "Image resolution",
      action:
        megapixels >= 1.2
          ? "Good. The image is strong enough for an online listing."
          : "Retake or upload a higher-resolution image.",
    },
    {
      good: stats.avgLightness > 0.25 && stats.avgLightness < 0.78,
      title: "Lighting",
      action:
        stats.avgLightness < 0.25
          ? "Retake in brighter natural light."
          : stats.avgLightness > 0.78
            ? "Reduce glare or overexposure."
            : "Good. Lighting looks usable.",
    },
    {
      good: stats.edgeScore > 0.12,
      title: "Sharpness",
      action:
        stats.edgeScore > 0.12
          ? "Good. The image appears sharp enough."
          : "Retake straight-on with a steady camera.",
    },
    {
      good: Boolean(file && file.size > 400000),
      title: "File quality",
      action:
        file?.size > 400000
          ? "Good. File size is suitable for analysis and listing."
          : "Use a larger original file if possible.",
    },
    {
      good: false,
      title: "Extra trust photos",
      action: "Add close-up texture, signature, side angle and frame photo before selling.",
    },
    {
      good: false,
      title: "Room mockup",
      action: "Show the painting in a real room. This is the strongest selling bridge on Art For My Room.",
    },
  ];
}

function buildReadinessScore({ file, form, stats, priceRange }) {
  const checklist = [
    {
      good: Boolean(file),
      label: "Painting image uploaded",
      action: "Upload a clear painting image.",
    },
    {
      good: stats.pixelWidth * stats.pixelHeight >= 1200000,
      label: "Image resolution is strong enough",
      action: "Use a higher-resolution image.",
    },
    {
      good: stats.avgLightness > 0.24 && stats.avgLightness < 0.8,
      label: "Lighting is usable",
      action: "Retake with balanced natural light.",
    },
    {
      good: stats.edgeScore > 0.12,
      label: "Image sharpness looks usable",
      action: "Retake straight-on with a steady camera.",
    },
    {
      good: Boolean(form.titleSuggestion.trim()),
      label: "Working title added",
      action: "Choose one of the generated titles.",
    },
    {
      good: form.medium !== "Other",
      label: "Medium selected",
      action: "Select the real painting medium.",
    },
    {
      good: Boolean(form.widthCm && form.heightCm),
      label: "Physical size added",
      action: "Add width and height.",
    },
    {
      good: Boolean(form.subject),
      label: "Subject selected",
      action: "Select the broad subject manually.",
    },
    {
      good: Boolean(priceRange),
      label: "Price strategy generated",
      action: "Add size to generate price guidance.",
    },
  ];

  const score = Math.round((checklist.filter((item) => item.good).length / checklist.length) * 100);

  return { score, checklist };
}

function makeTitleIdeas(form, paletteNames, mood) {
  const subject = form.subject || "painting";
  const base = form.titleSuggestion.trim();
  const main = paletteNames[0] || "colour";
  const second = paletteNames[1] || "light";

  return uniqueList([
    base,
    `A Study in ${main}`,
    `Where ${second} Settles`,
    `${main} and ${second}`,
    `The Quiet Room`,
    `After the Light Changed`,
    `A Place for ${main}`,
    `${mood.temperature[0].toUpperCase()}${mood.temperature.slice(1)} ${subject}`,
    `The Wall Remembers`,
    `Room for Silence`,
    `Between ${main} and Memory`,
    `Soft Architecture of Colour`,
  ]).slice(0, 10);
}

function makeActionPack({ form, stats, paletteNames, mood, technique, priceRange, titleIdeas }) {
  const title = form.titleSuggestion.trim() || titleIdeas[0] || "Untitled Painting";
  const subject = form.subject || "Abstract";
  const paletteText = paletteNames.slice(0, 4).join(", ") || "a layered palette";
  const sizeText = form.widthCm && form.heightCm ? `${form.widthCm} × ${form.heightCm} cm` : "size to be confirmed";

  const listingDescription =
`${title} is an original ${technique.technique.toLowerCase()} with a ${mood.phrase} presence. Led by ${paletteText}, the painting carries a ${mood.buyerFeeling} feeling and is suited to rooms that need ${mood.temperature === "cool" ? "calm, freshness and visual air" : mood.temperature === "warm" ? "warmth, character and presence" : "balance, atmosphere and softness"}.`;

  const shortCard =
`${title} · ${subject}. ${paletteNames.slice(0, 3).join(", ")} tones. A ${mood.buyerFeeling} original painting for a room that needs presence without noise.`;

  const instagram =
`A closer look at ${title}.

This painting is led by ${paletteNames.slice(0, 3).join(", ")} and has a ${mood.phrase} feeling.

It is not only about filling a wall. It is about changing the mood of a room.

Would you place it in a living room, bedroom or hallway?`;

  const facebook =
`${title}

${listingDescription}

Medium: ${form.medium}
Size: ${sizeText}
${priceRange ? `Suggested listing: ${priceRange.safeListingLabel}` : ""}

Message for details, close-up photos or a room mockup.`;

  const linkedIn =
`A painting changes when it is seen inside a room.

For ${title}, the strongest signals are ${paletteNames.slice(0, 3).join(", ")}, a ${mood.phrase} presence, and a subject direction of ${subject.toLowerCase()}.

That is why room context matters. A blank wall makes a painting look decorative. A real room makes it feel personal.

This is the idea behind Art For My Room: helping original paintings find the rooms where they naturally belong.`;

  const reelHook =
`This painting is not just for a wall. It is for a room that needs ${mood.temperature === "cool" ? "calm" : mood.temperature === "warm" ? "warmth" : "balance"}.`;

  const firstComment =
`Palette: ${paletteNames.slice(0, 5).join(", ")}
Mood: ${mood.phrase}
Room fit: living room, bedroom, hallway or studio wall`;

  const hashtags = uniqueList([
    "#originalpainting",
    "#paintingforsale",
    "#artistsoninstagram",
    "#contemporarypainting",
    "#interiorart",
    "#livingroomart",
    "#bedroomart",
    "#wallartdecor",
    `#${slugify(subject).replace(/-/g, "")}`,
    ...paletteNames.slice(0, 4).map((name) => `#${slugify(`${name} painting`).replace(/-/g, "")}`),
  ]).join(" ");

  const buyerReply =
`Hello,

Thank you for your interest in ${title}.

${shortCard}

The painting is ${form.medium.toLowerCase()}${form.widthCm && form.heightCm ? ` and measures ${form.widthCm} × ${form.heightCm} cm` : ""}. ${priceRange ? `The suggested listing price is ${priceRange.safeListingLabel}.` : ""}

I can also send close-up images, a room mockup, delivery details or payment information.

Best,
${form.artistName || "Artist"}`;

  const negotiationReply =
`Hello,

Thank you for your offer on ${title}. I appreciate your interest.

${priceRange ? `The current guide price is ${priceRange.safeListingLabel}, and the lowest room-offer level I would seriously consider is around ${priceRange.offerMinimumLabel}.` : "I am open to a respectful offer, depending on delivery and timing."}

If you would like, I can send a room mockup and close-up images before you decide.

Best,
${form.artistName || "Artist"}`;

  const roomFitText =
`${title} works best in a ${mood.temperature === "cool" ? "calm, light or coastal-inspired" : mood.temperature === "warm" ? "warm, characterful or earthy" : "balanced, minimal or soft contemporary"} room. It would suit a living room, bedroom, hallway or home office, especially on a ${mood.temperature === "warm" ? "cream, warm white, olive or soft beige" : "warm white, soft grey, stone or light neutral"} wall.`;

  const seoTitle = `${title} | ${paletteNames.slice(0, 2).join(" and ")} ${subject} artwork`;
  const metaDescription = `${title} is an original painting with ${paletteNames.slice(0, 3).join(", ")} tones and a ${mood.phrase} presence. Suitable for room decor, collectors and art buyers.`;
  const altText = `Original painting titled ${title}, ${subject.toLowerCase()}, with ${paletteNames.slice(0, 3).join(", ")} tones.`;
  const filename = `${slugify(title)}-${slugify(subject)}-${slugify(paletteNames[0] || "painting")}.jpg`;

  const buyerSearchPhrases = uniqueList([
    `${paletteNames[0] || "original"} painting for living room`,
    `${subject.toLowerCase()} painting for sale`,
    `${mood.temperature} artwork for bedroom`,
    `${mood.energy} wall art`,
    `original ${subject.toLowerCase()} artwork`,
    `artwork with ${paletteNames.slice(0, 2).join(" and ")} tones`,
    "painting to match my room",
    "original painting for home decor",
  ]).slice(0, 10);

  const contentPlan = [
    {
      day: "Day 1",
      action: "Post the finished painting",
      copy: instagram,
    },
    {
      day: "Day 3",
      action: "Post a close-up detail",
      copy: `Close-up of ${title}. The surface and palette are where the painting becomes physical, not just visual.`,
    },
    {
      day: "Day 5",
      action: "Ask a room question",
      copy: `Where would you place ${title}: living room, bedroom, hallway or reading corner?`,
    },
    {
      day: "Day 8",
      action: "Post the room-fit angle",
      copy: roomFitText,
    },
    {
      day: "Day 12",
      action: "Availability reminder",
      copy: `${title} is available. Message for size, price, close-ups or a room mockup.`,
    },
  ];

  return {
    title,
    listingDescription,
    shortCard,
    instagram,
    facebook,
    linkedIn,
    reelHook,
    firstComment,
    hashtags,
    buyerReply,
    negotiationReply,
    roomFitText,
    seoTitle,
    metaDescription,
    altText,
    filename,
    buyerSearchPhrases,
    contentPlan,
  };
}

function makeRoomFit({ form, mood, paletteNames }) {
  const width = Number(form.widthCm);
  const height = Number(form.heightCm);
  const maxSide = Math.max(width || 0, height || 0);

  const rooms = [];
  const walls = [];
  const placements = [];
  const styles = [];
  const objections = [];

  if (!width || !height) {
    rooms.push("living room", "bedroom", "hallway", "home office");
    placements.push("above a console table", "inside a gallery wall", "above a reading chair");
    objections.push("Buyer may ask about scale. Add exact dimensions and a room mockup.");
  } else if (maxSide <= 35) {
    rooms.push("hallway", "bedside wall", "shelf styling", "gallery wall");
    placements.push("above a bedside table", "inside a small gallery wall", "on a narrow wall");
  } else if (maxSide <= 75) {
    rooms.push("living room", "bedroom", "hallway", "home office");
    placements.push("above a console table", "above a reading chair", "on a medium feature wall");
  } else {
    rooms.push("large living room", "dining room", "studio", "feature wall");
    placements.push("above a sofa", "above a sideboard", "on a large statement wall");
    objections.push("Buyer may worry about delivery and hanging. Add delivery and hanging notes.");
  }

  if (mood.temperature === "cool") {
    walls.push("warm white", "soft grey", "stone", "light oak surroundings");
    styles.push("minimal", "coastal", "Scandinavian", "modern calm");
  } else if (mood.temperature === "warm") {
    walls.push("warm white", "cream", "soft beige", "olive", "deep burgundy accent");
    styles.push("classic", "rustic", "bohemian", "warm contemporary");
  } else {
    walls.push("warm white", "soft taupe", "light grey", "muted green");
    styles.push("modern", "transitional", "minimal", "soft contemporary");
  }

  if (paletteNames.includes("turquoise") || paletteNames.includes("blue")) {
    styles.push("coastal", "fresh modern", "calm bedroom");
  }

  if (paletteNames.includes("gold") || paletteNames.includes("orange")) {
    styles.push("warm contemporary", "earthy interiors", "classic hallway");
  }

  objections.push(
    "Some buyers hesitate when they cannot imagine the painting in their room. Use a room mockup.",
    "If the price is high, show texture, signature, frame and scale photos.",
    "If the work is subtle, sell the feeling it creates in the room, not only the subject."
  );

  return {
    rooms: uniqueList(rooms).slice(0, 7),
    walls: uniqueList(walls).slice(0, 7),
    placements: uniqueList(placements).slice(0, 5),
    styles: uniqueList(styles).slice(0, 8),
    objections: uniqueList(objections),
  };
}

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

function SectionTitle({ icon, label, title, text }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Chip
        icon={icon}
        label={label}
        sx={{
          mb: 1.5,
          bgcolor: TURQUOISE_MIST,
          color: TURQUOISE_DARK,
          fontWeight: 950,
          "& .MuiChip-icon": { color: TURQUOISE_DARK },
        }}
      />
      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontWeight: 950,
          fontSize: { xs: "1.7rem", md: "2.25rem" },
          letterSpacing: "-0.045em",
          lineHeight: 1,
        }}
      >
        {title}
      </Typography>
      {text && (
        <Typography sx={{ color: MUTED, lineHeight: 1.75, mt: 1 }}>
          {text}
        </Typography>
      )}
    </Box>
  );
}

function MiniStat({ icon, label, value, tone = "turquoise" }) {
  const isBurgundy = tone === "burgundy";
  const isGold = tone === "gold";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.8,
        borderRadius: 4,
        height: "100%",
        bgcolor: isBurgundy ? BURGUNDY_MIST : isGold ? GOLD_MIST : TURQUOISE_MIST,
        border: `1px solid ${
          isBurgundy ? alpha(BURGUNDY, 0.18) : isGold ? alpha(GOLD, 0.24) : alpha(TURQUOISE, 0.18)
        }`,
      }}
    >
      <Box sx={{ color: isBurgundy ? BURGUNDY : isGold ? GOLD_DARK : TURQUOISE_DARK, mb: 1 }}>
        {icon}
      </Box>
      <Typography sx={{ color: MUTED, fontSize: 12, fontWeight: 850 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 950, color: INK, lineHeight: 1.15 }}>{value}</Typography>
    </Paper>
  );
}

function CopyBlock({ title, text, onCopy, copied, minHeight = 120 }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 5,
        bgcolor: OFF_WHITE,
        border: `1px solid ${LINE}`,
        height: "100%",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Typography sx={{ fontWeight: 950, mb: 1 }}>{title}</Typography>
        <LightButton
          size="small"
          startIcon={copied ? <CheckCircleRoundedIcon /> : <ContentCopyRoundedIcon />}
          onClick={onCopy}
          sx={{ py: 0.55, px: 1.2 }}
        >
          {copied ? "Copied" : "Copy"}
        </LightButton>
      </Stack>
      <Typography
        sx={{
          whiteSpace: "pre-wrap",
          color: MUTED,
          lineHeight: 1.72,
          minHeight,
        }}
      >
        {text}
      </Typography>
    </Paper>
  );
}

function ListPanel({ title, items, icon, tone = "turquoise" }) {
  const bg = tone === "gold" ? GOLD_MIST : tone === "burgundy" ? BURGUNDY_MIST : TURQUOISE_MIST;
  const color = tone === "gold" ? GOLD_DARK : tone === "burgundy" ? BURGUNDY : TURQUOISE_DARK;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 5,
        bgcolor: WHITE,
        border: `1px solid ${LINE}`,
        height: "100%",
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.4 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 3.5,
            bgcolor: bg,
            color,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 950 }}>{title}</Typography>
      </Stack>

      <Stack spacing={1}>
        {items.map((item) => (
          <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
            <CheckCircleRoundedIcon sx={{ color, fontSize: 19, mt: 0.15 }} />
            <Typography sx={{ color: MUTED, lineHeight: 1.55 }}>{item}</Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

function PaletteSwatches({ palette }) {
  return (
    <Grid container spacing={1.4}>
      {palette.map((item) => (
        <Grid item xs={6} sm={4} md={3} key={`${item.hex}-${item.percentage}`}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              border: `1px solid ${LINE}`,
              bgcolor: WHITE,
            }}
          >
            <Box sx={{ height: 78, bgcolor: item.hex }} />
            <Box sx={{ p: 1.3 }}>
              <Typography sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                {item.name}
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: 13 }}>
                {item.hex} · {item.percentage}%
              </Typography>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

export default function ArtistPaintingAnalyzerPage() {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [submittingListing, setSubmittingListing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [copied, setCopied] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [dialog, setDialog] = useState({ open: false, type: "passport", title: "", message: "" });

  const [form, setForm] = useState({
    artistName: "",
    artistLevel: "emerging",
    medium: "Other",
    widthCm: "",
    heightCm: "",
    actualPrice: "",
    titleSuggestion: "",
    subject: "Abstract",
    roomStyle: "",
  });

  const [listing, setListing] = useState({
    artistName: "",
    artistEmail: "",
    country: "GB",
    title: "",
    medium: "",
    widthCm: "",
    heightCm: "",
    priceGbp: "",
    description: "",
    permissionToMatchRooms: true,
    agreeRights: false,
    agreeContact: false,
  });

  const endpoint = useMemo(() => buildApiUrl("artist-submissions/create/"), []);

  useEffect(() => {
    document.title = "Painter Sales Toolkit | Art For My Room";

    let meta = document.querySelector('meta[name="description"]');

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    meta.setAttribute(
      "content",
      "Free painter sales toolkit. Upload a painting and get listing text, price strategy, room-fit copy, social posts, buyer replies and a prefilled Art For My Room listing form."
    );
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const progress = useMemo(() => {
    const checks = [
      file,
      form.artistName,
      form.artistLevel,
      form.medium,
      form.widthCm,
      form.heightCm,
      form.subject,
    ];

    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [file, form]);

  const handleFormChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleListingChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setListing((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];

    setError("");
    setCopied("");
    setAnalysis(null);
    setActiveTab(0);

    if (!selected) return;

    const allowed = ["image/jpeg", "image/png", "image/webp"];

    if (!allowed.includes(selected.type)) {
      setError("Please upload a JPG, PNG or WEBP painting image.");
      return;
    }

    if (selected.size > 12 * 1024 * 1024) {
      setError("Please upload an image below 12MB.");
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);

    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
  };

  const buildPassport = async () => {
    if (!file) {
      setError("Please upload a painting image first.");
      return;
    }

    setLoading(true);
    setError("");
    setCopied("");

    try {
      const { img, url } = await loadImageFromFile(file);
      const stats = analysePixels(img);
      URL.revokeObjectURL(url);

      const paletteNames = uniqueList(stats.palette.map((item) => item.name));
      const mood = getMood(stats);
      const technique = guessTechnique(stats, form.medium);
      const orientation = getOrientation(stats.pixelWidth, stats.pixelHeight);
      const priceRange = estimatePrice(form, stats);
      const titleIdeas = makeTitleIdeas(form, paletteNames, mood);
      const actionPack = makeActionPack({
        form,
        stats,
        paletteNames,
        mood,
        technique,
        priceRange,
        titleIdeas,
      });
      const roomFit = makeRoomFit({ form, mood, paletteNames });
      const photoChecklist = buildPhotoChecklist(stats, file);
      const readiness = buildReadinessScore({ file, form, stats, priceRange });

      const newAnalysis = {
        ...stats,
        paletteNames,
        mood,
        technique,
        orientation,
        priceRange,
        titleIdeas,
        actionPack,
        roomFit,
        photoChecklist,
        readiness,
      };

      setAnalysis(newAnalysis);

      setListing((prev) => ({
        ...prev,
        artistName: form.artistName || prev.artistName,
        title: actionPack.title,
        medium: form.medium !== "Other" ? form.medium : technique.technique,
        widthCm: form.widthCm,
        heightCm: form.heightCm,
        priceGbp:
          form.actualPrice ||
          (priceRange ? String(priceRange.safeListing) : prev.priceGbp),
        description: actionPack.listingDescription,
        permissionToMatchRooms: true,
      }));

      try {
        localStorage.setItem(
          "artForMyRoomPaintingAnalyzerDraft",
          JSON.stringify({
            createdAt: new Date().toISOString(),
            form,
            listing: {
              title: actionPack.title,
              medium: form.medium !== "Other" ? form.medium : technique.technique,
              widthCm: form.widthCm,
              heightCm: form.heightCm,
              priceGbp: form.actualPrice || (priceRange ? String(priceRange.safeListing) : ""),
              description: actionPack.listingDescription,
            },
            palette: stats.palette,
            mood,
          })
        );
      } catch {
        // localStorage is helpful, not essential.
      }

      setActiveTab(0);
      setDialog({
        open: true,
        type: "passport",
        title: "Painter toolkit ready",
        message:
          "Your action-ready selling kit is ready. You now have listing copy, social posts, buyer replies, room-fit text, price strategy and a prefilled listing form.",
      });
    } catch (err) {
      setError(err.message || "Could not analyse this painting.");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setFile(null);
    setPreviewUrl("");
    setAnalysis(null);
    setError("");
    setCopied("");
    setActiveTab(0);
    setDialog({ open: false, type: "passport", title: "", message: "" });

    setForm({
      artistName: "",
      artistLevel: "emerging",
      medium: "Other",
      widthCm: "",
      heightCm: "",
      actualPrice: "",
      titleSuggestion: "",
      subject: "Abstract",
      roomStyle: "",
    });

    setListing({
      artistName: "",
      artistEmail: "",
      country: "GB",
      title: "",
      medium: "",
      widthCm: "",
      heightCm: "",
      priceGbp: "",
      description: "",
      permissionToMatchRooms: true,
      agreeRights: false,
      agreeContact: false,
    });

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const copyText = async (label, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      setTimeout(() => setCopied(""), 1800);
    } catch {
      setError("Could not copy text. Please copy it manually.");
    }
  };

  const submitListing = async () => {
    if (!file) {
      setError("Please upload the painting image first.");
      return;
    }

    if (!listing.artistName.trim()) {
      setError("Please add the artist name or initials before submitting.");
      return;
    }

    if (!listing.artistEmail.trim()) {
      setError("Please add the artist email before submitting.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(listing.artistEmail.trim())) {
      setError("Please add a valid artist email.");
      return;
    }

    if (!listing.title.trim()) {
      setError("Please add the painting title before submitting.");
      return;
    }

    if (!listing.priceGbp) {
      setError("Please add a listed price before submitting.");
      return;
    }

    if (!listing.agreeRights || !listing.agreeContact) {
      setError("Please confirm artwork rights and email contact permission.");
      return;
    }

    setSubmittingListing(true);
    setError("");

    try {
      const data = new FormData();

      data.append("artwork_category", "painting");
      data.append("category", "painting");
      data.append("artwork_type", "painting");
      data.append("submission_type", "painting");
      data.append("placement_type", "wall");

      data.append("artistName", listing.artistName.trim());
      data.append("artist_name", listing.artistName.trim());
      data.append("email", listing.artistEmail.trim());
      data.append("artist_email", listing.artistEmail.trim());

      data.append("paintingTitle", listing.title.trim());
      data.append("artworkTitle", listing.title.trim());
      data.append("title", listing.title.trim());

      data.append("medium", listing.medium.trim());
      data.append("description", listing.description.trim());
      data.append("story", listing.description.trim());

      data.append("width_cm", String(listing.widthCm || ""));
      data.append("height_cm", String(listing.heightCm || ""));
      data.append("dimensions", listing.widthCm && listing.heightCm ? `${listing.widthCm} x ${listing.heightCm} cm` : "");

      data.append("price_gbp", String(listing.priceGbp || ""));
      data.append("askingPrice", String(listing.priceGbp || ""));

      data.append("country", listing.country);
      data.append("location_country", listing.country);
      data.append("location", listing.country);

      data.append("for_sale", "true");
      data.append("buy_now_enabled", "true");
      data.append("permission_to_match_rooms", listing.permissionToMatchRooms ? "true" : "false");
      data.append("canRoomApp", listing.permissionToMatchRooms ? "true" : "false");
      data.append("wantsRoomMockup", listing.permissionToMatchRooms ? "true" : "false");
      data.append("wantsSalesListing", "true");
      data.append("wantsArtworkDescription", "true");

      data.append("image", file);
      data.append("paintingImage", file);
      data.append("artworkImage", file);

      const response = await fetch(endpoint, {
        method: "POST",
        body: data,
      });

      let payload = {};

      try {
        payload = await response.json();
      } catch {
        payload = {};
      }

      if (!response.ok) {
        throw new Error(
          payload.message ||
            payload.error ||
            `Submission failed with status ${response.status}.`
        );
      }

      setDialog({
        open: true,
        type: "submitted",
        title: "Painting submitted",
        message:
          payload.message ||
          "Your painting has been submitted for review. The artist should receive a confirmation email with the submitted details.",
      });

      setActiveTab(6);
    } catch (err) {
      setError(err.message || "Could not submit the painting. Please try again.");
    } finally {
      setSubmittingListing(false);
    }
  };

  return (
    <PageWrap>
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
        <HeroWrap elevation={0}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                icon={<AutoAwesomeRoundedIcon />}
                label="Free value for painters"
                sx={{
                  mb: 2.5,
                  bgcolor: alpha(WHITE, 0.12),
                  color: WHITE,
                  border: `1px solid ${alpha(WHITE, 0.16)}`,
                  fontWeight: 900,
                  fontFamily: MONO_FONT,
                  "& .MuiChip-icon": { color: GOLD },
                }}
              />

              <Typography
                component="h1"
                sx={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 950,
                  fontSize: { xs: "2.45rem", sm: "3.3rem", md: "5rem" },
                  lineHeight: 0.96,
                  letterSpacing: "-0.07em",
                  mb: 2,
                }}
              >
                Painter Sales Toolkit
              </Typography>

              <Typography
                sx={{
                  color: alpha(WHITE, 0.82),
                  fontSize: { xs: "1rem", md: "1.16rem" },
                  lineHeight: 1.9,
                  maxWidth: 850,
                  mb: 3,
                }}
              >
                Upload one painting. Get copy-ready listing text, price strategy,
                room-fit copy, social posts, buyer replies, SEO tags and a
                prefilled Art For My Room listing form. Less admin. More art.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.4}>
                <GradientButton
                  startIcon={<CloudUploadRoundedIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload painting
                </GradientButton>

                <LightButton
                  startIcon={<RocketLaunchRoundedIcon />}
                  onClick={() => {
                    document.getElementById("toolkit")?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }}
                  sx={{
                    color: WHITE,
                    borderColor: alpha(WHITE, 0.34),
                    background: alpha(WHITE, 0.08),
                    "&:hover": {
                      background: alpha(WHITE, 0.16),
                      borderColor: WHITE,
                    },
                  }}
                >
                  Open toolkit
                </LightButton>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <DarkCard sx={{ animation: `${float} 7s ease-in-out infinite` }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                    {[
                      {
                        icon: <WorkspacePremiumRoundedIcon />,
                        title: "Useful immediately",
                        text: "Copy posts, listing text, buyer replies and room-fit copy.",
                      },
                      {
                        icon: <Inventory2RoundedIcon />,
                        title: "Prefilled listing",
                        text: "The analyzer prepares the upload form so artists do less work.",
                      },
                      {
                        icon: <HomeWorkRoundedIcon />,
                        title: "Room-first selling",
                        text: "Connect the painting to real rooms, not only gallery language.",
                      },
                    ].map((item) => (
                      <Paper
                        key={item.title}
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: 4,
                          bgcolor: alpha(WHITE, 0.1),
                          border: `1px solid ${alpha(WHITE, 0.13)}`,
                          color: WHITE,
                        }}
                      >
                        <Stack direction="row" spacing={1.4} alignItems="center">
                          <Box sx={{ color: GOLD }}>{item.icon}</Box>
                          <Box>
                            <Typography sx={{ fontWeight: 950 }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ color: alpha(WHITE, 0.7), fontSize: 14 }}>
                              {item.text}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </CardContent>
              </DarkCard>
            </Grid>
          </Grid>
        </HeroWrap>

        <Grid container spacing={4} sx={{ mt: 4 }} id="toolkit">
          <Grid item xs={12} lg={5}>
            <GlassCard>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <SectionTitle
                  icon={<PaletteRoundedIcon />}
                  label="Step 1"
                  title="Add painting details"
                  text="The painter gives the truth. The tool turns it into action-ready selling material."
                />

                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 900 }}>Toolkit strength</Typography>
                  <Typography sx={{ fontWeight: 950, color: TURQUOISE_DARK }}>
                    {progress}%
                  </Typography>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 10,
                    borderRadius: 999,
                    bgcolor: alpha(TURQUOISE, 0.12),
                    mb: 3,
                    "& .MuiLinearProgress-bar": {
                      background: `linear-gradient(90deg, ${TURQUOISE}, ${GOLD}, ${BURGUNDY})`,
                    },
                  }}
                />

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={handleFileChange}
                />

                <UploadArea elevation={0} onClick={() => fileInputRef.current?.click()}>
                  <CloudUploadRoundedIcon
                    sx={{ fontSize: 56, color: TURQUOISE_DARK, mb: 1 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: "1.25rem",
                    }}
                  >
                    {file ? file.name : "Choose painting image"}
                  </Typography>
                  <Typography sx={{ color: MUTED, mt: 0.8 }}>
                    JPG, PNG or WEBP. A clear straight-on image works best.
                  </Typography>
                </UploadArea>

                {previewUrl && (
                  <PreviewFrame sx={{ mt: 3 }}>
                    <Box
                      component="img"
                      src={previewUrl}
                      alt="Painting preview"
                      sx={{
                        display: "block",
                        width: "100%",
                        maxHeight: 520,
                        objectFit: "cover",
                      }}
                    />
                  </PreviewFrame>
                )}

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12}>
                    <Field
                      fullWidth
                      label="Artist name or initials"
                      value={form.artistName}
                      onChange={handleFormChange("artistName")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BrushRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      fullWidth
                      label="Painting title, optional"
                      value={form.titleSuggestion}
                      onChange={handleFormChange("titleSuggestion")}
                      helperText="Leave empty and the tool will suggest titles."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LightbulbRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      select
                      fullWidth
                      label="Artist level"
                      value={form.artistLevel}
                      onChange={handleFormChange("artistLevel")}
                    >
                      {artistLevels.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      select
                      fullWidth
                      label="Medium"
                      value={form.medium}
                      onChange={handleFormChange("medium")}
                    >
                      {mediumOptions.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      fullWidth
                      label="Width cm"
                      type="number"
                      value={form.widthCm}
                      onChange={handleFormChange("widthCm")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <StraightenRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      fullWidth
                      label="Height cm"
                      type="number"
                      value={form.heightCm}
                      onChange={handleFormChange("heightCm")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhotoSizeSelectLargeRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      fullWidth
                      label="Current price, optional"
                      type="number"
                      value={form.actualPrice}
                      onChange={handleFormChange("actualPrice")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SellRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      select
                      fullWidth
                      label="Subject / motif"
                      value={form.subject}
                      onChange={handleFormChange("subject")}
                      helperText="Manual subject is more reliable for paintings."
                    >
                      {subjectOptions.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>

                  <Grid item xs={12}>
                    <Field
                      fullWidth
                      label="Interior style, optional"
                      value={form.roomStyle}
                      onChange={handleFormChange("roomStyle")}
                      helperText="Example: coastal, minimal, rustic, classic, bohemian, modern."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeWorkRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {error && (
                  <Alert severity="error" sx={{ borderRadius: 3, mt: 2 }}>
                    {error}
                  </Alert>
                )}

                {copied && (
                  <Alert severity="success" sx={{ borderRadius: 3, mt: 2 }}>
                    {copied} copied.
                  </Alert>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.3} sx={{ mt: 3 }}>
                  <GradientButton
                    onClick={buildPassport}
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <InsightsRoundedIcon />
                      )
                    }
                  >
                    {loading ? "Creating toolkit..." : "Create selling kit"}
                  </GradientButton>

                  <LightButton startIcon={<RestartAltRoundedIcon />} onClick={resetAll}>
                    Reset
                  </LightButton>
                </Stack>
              </CardContent>
            </GlassCard>
          </Grid>

          <Grid item xs={12} lg={7}>
            {!analysis ? (
              <DarkCard>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Stack
                    spacing={3}
                    alignItems="center"
                    textAlign="center"
                    sx={{ py: { xs: 4, md: 8 } }}
                  >
                    <ImageSearchRoundedIcon sx={{ fontSize: 76, color: GOLD }} />
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: HEADING_FONT,
                          fontWeight: 950,
                          fontSize: { xs: "2rem", md: "3rem" },
                          letterSpacing: "-0.05em",
                          mb: 1,
                        }}
                      >
                        Action-ready painter value appears here
                      </Typography>
                      <Typography
                        sx={{
                          color: alpha(WHITE, 0.72),
                          lineHeight: 1.85,
                          maxWidth: 720,
                        }}
                      >
                        The goal is not a report. The goal is useful material a painter can post,
                        send to buyers, use for SEO, and submit to Art For My Room with less work.
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </DarkCard>
            ) : (
              <GlassCard>
                <CardContent sx={{ p: { xs: 2.4, md: 3.2 } }}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    alignItems={{ xs: "stretch", md: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: HEADING_FONT,
                          fontWeight: 950,
                          fontSize: { xs: "1.8rem", md: "2.4rem" },
                          letterSpacing: "-0.045em",
                          lineHeight: 1,
                        }}
                      >
                        Your Selling Kit
                      </Typography>
                      <Typography sx={{ color: MUTED, mt: 0.6 }}>
                        Copy it. Post it. Send it. List it.
                      </Typography>
                    </Box>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <LightButton
                        startIcon={<ContentCopyRoundedIcon />}
                        onClick={() => copyText("Listing text", analysis.actionPack.listingDescription)}
                      >
                        Copy listing
                      </LightButton>
                      <BurgundyButton
                        startIcon={<RocketLaunchRoundedIcon />}
                        onClick={() => setActiveTab(6)}
                      >
                        List painting
                      </BurgundyButton>
                    </Stack>
                  </Stack>

                  <Tabs
                    value={activeTab}
                    onChange={(event, value) => setActiveTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      borderBottom: `1px solid ${LINE}`,
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 900,
                        color: MUTED,
                      },
                      "& .Mui-selected": {
                        color: BURGUNDY,
                      },
                      "& .MuiTabs-indicator": {
                        bgcolor: BURGUNDY,
                      },
                    }}
                  >
                    <Tab label="Ready to Sell" />
                    <Tab label="Listing Text" />
                    <Tab label="Post Today" />
                    <Tab label="Buyer Replies" />
                    <Tab label="Room Fit" />
                    <Tab label="Price Strategy" />
                    <Tab label="List Now" />
                  </Tabs>

                  <TabPanel value={activeTab} index={0}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <MiniStat
                          icon={<VerifiedRoundedIcon />}
                          label="Readiness"
                          value={`${analysis.readiness.score}%`}
                          tone={analysis.readiness.score >= 75 ? "turquoise" : "gold"}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MiniStat
                          icon={<CameraAltRoundedIcon />}
                          label="Image"
                          value={`${analysis.pixelWidth} × ${analysis.pixelHeight}px`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MiniStat
                          icon={<PsychologyRoundedIcon />}
                          label="Mood"
                          value={analysis.mood.phrase}
                          tone="gold"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <MiniStat
                          icon={<CategoryRoundedIcon />}
                          label="Subject"
                          value={form.subject}
                          tone="burgundy"
                        />
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 3 }}>
                      <Typography sx={{ fontWeight: 950, mb: 1.5 }}>
                        Palette to use in listing and hashtags
                      </Typography>
                      <PaletteSwatches palette={analysis.palette} />
                    </Box>

                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={12} md={6}>
                        <ListPanel
                          title="Already useful"
                          icon={<ChecklistRoundedIcon />}
                          items={analysis.readiness.checklist
                            .filter((item) => item.good)
                            .map((item) => item.label)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ListPanel
                          title="Fix before selling"
                          icon={<AddPhotoAlternateRoundedIcon />}
                          items={analysis.readiness.checklist
                            .filter((item) => !item.good)
                            .map((item) => item.action)}
                          tone="burgundy"
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>

                  <TabPanel value={activeTab} index={1}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Listing description"
                          text={analysis.actionPack.listingDescription}
                          copied={copied === "Listing description"}
                          onCopy={() => copyText("Listing description", analysis.actionPack.listingDescription)}
                          minHeight={145}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Short card text"
                          text={analysis.actionPack.shortCard}
                          copied={copied === "Short card text"}
                          onCopy={() => copyText("Short card text", analysis.actionPack.shortCard)}
                          minHeight={70}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="SEO title"
                          text={analysis.actionPack.seoTitle}
                          copied={copied === "SEO title"}
                          onCopy={() => copyText("SEO title", analysis.actionPack.seoTitle)}
                          minHeight={55}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Meta description"
                          text={analysis.actionPack.metaDescription}
                          copied={copied === "Meta description"}
                          onCopy={() => copyText("Meta description", analysis.actionPack.metaDescription)}
                          minHeight={75}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Alt text"
                          text={analysis.actionPack.altText}
                          copied={copied === "Alt text"}
                          onCopy={() => copyText("Alt text", analysis.actionPack.altText)}
                          minHeight={60}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Suggested image filename"
                          text={analysis.actionPack.filename}
                          copied={copied === "Filename"}
                          onCopy={() => copyText("Filename", analysis.actionPack.filename)}
                          minHeight={55}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: 950, mb: 1.5 }}>
                          Title ideas
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          {analysis.titleIdeas.map((item) => (
                            <Chip
                              key={item}
                              label={item}
                              onClick={() => copyText("Title idea", item)}
                              sx={{
                                bgcolor: TURQUOISE_MIST,
                                color: TURQUOISE_DARK,
                                fontWeight: 850,
                                cursor: "pointer",
                              }}
                            />
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>
                  </TabPanel>

                  <TabPanel value={activeTab} index={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Instagram caption"
                          text={analysis.actionPack.instagram}
                          copied={copied === "Instagram caption"}
                          onCopy={() => copyText("Instagram caption", analysis.actionPack.instagram)}
                          minHeight={170}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Facebook post"
                          text={analysis.actionPack.facebook}
                          copied={copied === "Facebook post"}
                          onCopy={() => copyText("Facebook post", analysis.actionPack.facebook)}
                          minHeight={150}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="LinkedIn post"
                          text={analysis.actionPack.linkedIn}
                          copied={copied === "LinkedIn post"}
                          onCopy={() => copyText("LinkedIn post", analysis.actionPack.linkedIn)}
                          minHeight={190}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Reel hook"
                          text={analysis.actionPack.reelHook}
                          copied={copied === "Reel hook"}
                          onCopy={() => copyText("Reel hook", analysis.actionPack.reelHook)}
                          minHeight={70}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="First comment"
                          text={analysis.actionPack.firstComment}
                          copied={copied === "First comment"}
                          onCopy={() => copyText("First comment", analysis.actionPack.firstComment)}
                          minHeight={85}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Hashtags"
                          text={analysis.actionPack.hashtags}
                          copied={copied === "Hashtags"}
                          onCopy={() => copyText("Hashtags", analysis.actionPack.hashtags)}
                          minHeight={75}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ListPanel
                          title="5-post content plan"
                          icon={<CalendarMonthRoundedIcon />}
                          items={analysis.actionPack.contentPlan.map(
                            (item) => `${item.day}: ${item.action}. ${item.copy}`
                          )}
                          tone="gold"
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>

                  <TabPanel value={activeTab} index={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Reply to buyer enquiry"
                          text={analysis.actionPack.buyerReply}
                          copied={copied === "Buyer reply"}
                          onCopy={() => copyText("Buyer reply", analysis.actionPack.buyerReply)}
                          minHeight={220}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Reply to negotiation"
                          text={analysis.actionPack.negotiationReply}
                          copied={copied === "Negotiation reply"}
                          onCopy={() => copyText("Negotiation reply", analysis.actionPack.negotiationReply)}
                          minHeight={200}
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>

                  <TabPanel value={activeTab} index={4}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <CopyBlock
                          title="Room-fit selling text"
                          text={analysis.actionPack.roomFitText}
                          copied={copied === "Room-fit text"}
                          onCopy={() => copyText("Room-fit text", analysis.actionPack.roomFitText)}
                          minHeight={100}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ListPanel
                          title="Best rooms"
                          icon={<HomeWorkRoundedIcon />}
                          items={analysis.roomFit.rooms}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ListPanel
                          title="Wall colours"
                          icon={<PaletteRoundedIcon />}
                          items={analysis.roomFit.walls}
                          tone="gold"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ListPanel
                          title="Placement ideas"
                          icon={<PhotoSizeSelectLargeRoundedIcon />}
                          items={analysis.roomFit.placements}
                          tone="burgundy"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ListPanel
                          title="Interior styles"
                          icon={<HomeWorkRoundedIcon />}
                          items={analysis.roomFit.styles}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ListPanel
                          title="Buyer objections to solve"
                          icon={<ShieldRoundedIcon />}
                          items={analysis.roomFit.objections}
                          tone="gold"
                        />
                      </Grid>
                    </Grid>
                  </TabPanel>

                  <TabPanel value={activeTab} index={5}>
                    {analysis.priceRange ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <MiniStat
                            icon={<SellRoundedIcon />}
                            label="Guide range"
                            value={analysis.priceRange.label}
                            tone="burgundy"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <MiniStat
                            icon={<VerifiedRoundedIcon />}
                            label="Safe listing"
                            value={analysis.priceRange.safeListingLabel}
                            tone="gold"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <MiniStat
                            icon={<LocalOfferRoundedIcon />}
                            label="Offer minimum"
                            value={analysis.priceRange.offerMinimumLabel}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <MiniStat
                            icon={<RocketLaunchRoundedIcon />}
                            label="Faster sale"
                            value={analysis.priceRange.fasterSaleLabel}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Alert severity="info" sx={{ borderRadius: 3 }}>
                            Ambitious price if framed, exhibited, or strongly presented:
                            {" "}
                            <strong>{analysis.priceRange.ambitiousLabel}</strong>
                          </Alert>
                        </Grid>
                        {analysis.priceRange.currentPriceNote && (
                          <Grid item xs={12}>
                            <Alert severity="warning" sx={{ borderRadius: 3 }}>
                              {analysis.priceRange.currentPriceNote}
                            </Alert>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Alert severity="warning" sx={{ borderRadius: 3 }}>
                            This is pricing guidance, not a formal valuation. Adjust for materials,
                            framing, exhibition history, collector demand and your own confidence.
                          </Alert>
                        </Grid>
                      </Grid>
                    ) : (
                      <Alert severity="info" sx={{ borderRadius: 3 }}>
                        Add physical width and height to generate a pricing strategy.
                      </Alert>
                    )}
                  </TabPanel>

                  <TabPanel value={activeTab} index={6}>
                    <Alert severity="success" sx={{ borderRadius: 3, mb: 2 }}>
                      Half the upload work is already done. Review the prefilled fields, add email,
                      confirm rights, then submit the painting for review.
                    </Alert>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          required
                          label="Artist name or initials"
                          value={listing.artistName}
                          onChange={handleListingChange("artistName")}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BrushRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          required
                          type="email"
                          label="Artist email"
                          value={listing.artistEmail}
                          onChange={handleListingChange("artistEmail")}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AlternateEmailRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Field
                          fullWidth
                          required
                          select
                          label="Country where the painting is located"
                          value={listing.country}
                          onChange={handleListingChange("country")}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PublicRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                              </InputAdornment>
                            ),
                          }}
                        >
                          {countryOptions.map((item) => (
                            <MenuItem key={item.value} value={item.value}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>

                      <Grid item xs={12}>
                        <Field
                          fullWidth
                          required
                          label="Painting title"
                          value={listing.title}
                          onChange={handleListingChange("title")}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          label="Medium"
                          value={listing.medium}
                          onChange={handleListingChange("medium")}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          required
                          type="number"
                          label="Listed price GBP"
                          value={listing.priceGbp}
                          onChange={handleListingChange("priceGbp")}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SellRoundedIcon sx={{ color: TURQUOISE_DARK }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          type="number"
                          label="Width cm"
                          value={listing.widthCm}
                          onChange={handleListingChange("widthCm")}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          fullWidth
                          type="number"
                          label="Height cm"
                          value={listing.heightCm}
                          onChange={handleListingChange("heightCm")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Field
                          fullWidth
                          multiline
                          minRows={5}
                          label="Description"
                          value={listing.description}
                          onChange={handleListingChange("description")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 5,
                            bgcolor: OFF_WHITE,
                            border: `1px solid ${LINE}`,
                          }}
                        >
                          <Stack spacing={0.5}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={listing.permissionToMatchRooms}
                                  onChange={handleListingChange("permissionToMatchRooms")}
                                  sx={{
                                    color: TURQUOISE_DARK,
                                    "&.Mui-checked": { color: TURQUOISE_DARK },
                                  }}
                                />
                              }
                              label="Allow this painting to be matched with buyer room photos after review"
                            />

                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={listing.agreeRights}
                                  onChange={handleListingChange("agreeRights")}
                                  sx={{
                                    color: BURGUNDY,
                                    "&.Mui-checked": { color: BURGUNDY },
                                  }}
                                />
                              }
                              label="I confirm I own the rights to submit this painting"
                            />

                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={listing.agreeContact}
                                  onChange={handleListingChange("agreeContact")}
                                  sx={{
                                    color: BURGUNDY,
                                    "&.Mui-checked": { color: BURGUNDY },
                                  }}
                                />
                              }
                              label="I agree to be contacted by email about this submission"
                            />
                          </Stack>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                          <BurgundyButton
                            disabled={submittingListing}
                            startIcon={
                              submittingListing ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <SendRoundedIcon />
                              )
                            }
                            onClick={submitListing}
                          >
                            {submittingListing ? "Submitting..." : "Submit painting to Art For My Room"}
                          </BurgundyButton>

                          <LightButton
                            startIcon={<ContentCopyRoundedIcon />}
                            onClick={() => copyText("Prefilled listing", JSON.stringify(listing, null, 2))}
                          >
                            Copy fields
                          </LightButton>
                        </Stack>
                      </Grid>
                    </Grid>
                  </TabPanel>
                </CardContent>
              </GlassCard>
            )}
          </Grid>
        </Grid>
      </Container>

      <Dialog
        open={dialog.open}
        onClose={() => setDialog((prev) => ({ ...prev, open: false }))}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 7, overflow: "hidden" },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.7, md: 3.3 } }}>
          <Stack spacing={2.2}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box
                sx={{
                  width: 62,
                  height: 62,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: dialog.type === "submitted" ? BURGUNDY_MIST : TURQUOISE_MIST,
                  color: dialog.type === "submitted" ? BURGUNDY : TURQUOISE_DARK,
                  border: `1px solid ${
                    dialog.type === "submitted"
                      ? alpha(BURGUNDY, 0.25)
                      : alpha(TURQUOISE, 0.25)
                  }`,
                }}
              >
                <CheckCircleRoundedIcon sx={{ fontSize: 38 }} />
              </Box>

              <Button
                onClick={() => setDialog((prev) => ({ ...prev, open: false }))}
                sx={{
                  minWidth: 0,
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  color: MUTED,
                }}
              >
                <CloseRoundedIcon />
              </Button>
            </Stack>

            <Box>
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 950,
                  fontSize: "1.75rem",
                  letterSpacing: "-0.05em",
                  lineHeight: 1,
                }}
              >
                {dialog.title}
              </Typography>

              <Typography sx={{ color: MUTED, lineHeight: 1.75, mt: 1.2 }}>
                {dialog.message}
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
              {dialog.type === "passport" ? (
                <>
                  <GradientButton
                    fullWidth
                    onClick={() => {
                      setDialog((prev) => ({ ...prev, open: false }));
                      setActiveTab(6);
                    }}
                  >
                    List painting
                  </GradientButton>

                  <LightButton
                    fullWidth
                    onClick={() => {
                      setDialog((prev) => ({ ...prev, open: false }));
                      setActiveTab(2);
                    }}
                  >
                    Copy posts
                  </LightButton>
                </>
              ) : (
                <GradientButton
                  fullWidth
                  onClick={() => setDialog((prev) => ({ ...prev, open: false }))}
                >
                  Done
                </GradientButton>
              )}
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      <Box
        component="footer"
        sx={{
          bgcolor: INK,
          color: WHITE,
          mt: 6,
          py: 6,
        }}
      >
        <Container maxWidth="lg">
          <Stack spacing={2.2} alignItems="center" textAlign="center">
            <Chip
              label="Art For My Room"
              sx={{
                bgcolor: alpha(WHITE, 0.1),
                color: WHITE,
                border: `1px solid ${alpha(WHITE, 0.16)}`,
                fontWeight: 950,
              }}
            />

            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontWeight: 950,
                fontSize: { xs: "2rem", md: "2.7rem" },
                letterSpacing: "-0.055em",
                lineHeight: 1,
              }}
            >
              Free value first. Easy listing next.
            </Typography>

            <Typography sx={{ color: alpha(WHITE, 0.72), maxWidth: 780, lineHeight: 1.75 }}>
              The Painter Sales Toolkit helps artists turn one painting into listing text,
              social posts, room-fit copy, buyer replies and a prefilled upload form.
              Listing is free forever. Art For My Room only takes 10% when sold.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
              <GradientButton onClick={() => fileInputRef.current?.click()}>
                Analyze another painting
              </GradientButton>

              <LightButton
                component="a"
                href={`mailto:${CONTACT_EMAIL}`}
                sx={{
                  bgcolor: alpha(WHITE, 0.08),
                  color: WHITE,
                  borderColor: alpha(WHITE, 0.24),
                  "&:hover": {
                    bgcolor: alpha(WHITE, 0.14),
                  },
                }}
              >
                Email us
              </LightButton>
            </Stack>

            <Divider sx={{ width: "100%", borderColor: alpha(WHITE, 0.12), pt: 2 }} />

            <Typography sx={{ color: alpha(WHITE, 0.42), fontFamily: MONO_FONT, fontSize: 13 }}>
              © {new Date().getFullYear()} {SITE_NAME} · turquoise, gold and burgundy
            </Typography>
          </Stack>
        </Container>
      </Box>
    </PageWrap>
  );
}
